// Chakra imports
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Button,
  Text,
  useDisclosure,
  Flex,
  Box,
  SimpleGrid,
} from "@chakra-ui/react";
import { toastError, presentMutationError, toastWarning } from "../../../utils/showApiErrorToast";
import { toast } from "sonner";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import SignalStreamCard, { AssetData } from "./components/SignalStreamCard";
import { Signal } from "./components/SignalCard";
import AssetIcon from "./components/AssetIcon";
import SignalDetailsModal from "./components/SignalDetailsModal";
import RequireAdmin from "@/components/Access/RequireAdmin";
import { PageRouteHeadline } from "@/components/Layout/PageRouteHeadline";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { TradeFormData, Trade } from "../../../types/trade";
import TradeLogMiniStatistics from "../TradeLog/components/TradeLogMiniStatistics";
import { useUser } from "@clerk/clerk-react";
import { fetchTrades, closeTrade, closeAllTrades } from "../../../services/tradeApi";
import { notifyTradesUpdated } from "../../../hooks/useTradeStatistics";
import { executeSignalTrade } from "../../../services/signalsApi";
import { backendTradeToFrontendTrade } from "../../../services/tradeStorage";
import { useActiveBrokerAccount } from "../../../hooks/useActiveBrokerAccount";
import { useLoadActiveBrokerSnapshot } from "../../../hooks/useLoadActiveBrokerSnapshot";
import { useSignalsContext } from "../../../contexts/SignalsContext";
import { useDisplayCurrency } from "../../../contexts/DisplayCurrencyContext";

const generateChartData = (baseValue: number, trend: "up" | "down" = "up"): number[] => {
  const data: number[] = [];
  const points = 20;
  const variance = baseValue * 0.02;
  
  for (let i = 0; i < points; i++) {
    const random = (Math.random() - 0.5) * 2 * variance;
    const trendFactor = trend === "up" 
      ? (i / points) * variance * 0.5 
      : -(i / points) * variance * 0.5;
    data.push(baseValue + random + trendFactor);
  }
  return data;
};

const mockAssetsData = [
  {
    symbol: "XAU/USD",
    price: "$2,650.00",
    baseValue: 2650,
    trend: "up" as const,
  },
];

// Removed generateRandomSignal and generateInitialSignals - signals now come from WebSocket

function Tables() {
  const shadcnInset = useShadcnInsetChrome();
  const { formatMoney } = useDisplayCurrency();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Active trade account (all brokers — not Liquid-only credentials).
  const { user } = useUser();
  const { activeAccountCode: brokerAccountCode } = useActiveBrokerAccount();
  useLoadActiveBrokerSnapshot({ refresh: true });

  // Fetch trades for open trades count and unrealised PnL
  const [trades, setTrades] = useState<Trade[]>([]);
  const [isLoadingTrades, setIsLoadingTrades] = useState(false);

  useEffect(() => {
    const loadTrades = async () => {
      if (!user?.id || !brokerAccountCode) {
        setTrades([]);
        return;
      }

      try {
        setIsLoadingTrades(true);
        const response = await fetchTrades({
          broker_account_code: brokerAccountCode,
          status: "open",
          limit: 500,
          offset: 0,
        });
        const frontendTrades = response.trades.map(backendTradeToFrontendTrade);
        setTrades(frontendTrades);
      } catch (error) {
        console.error("Error loading trades:", error);
        setTrades([]);
      } finally {
        setIsLoadingTrades(false);
      }
    };

    loadTrades();
    
    const handleTradesUpdated = (event: Event) => {
      const action = (event as CustomEvent<{ action?: string }>)?.detail?.action;
      if (action === "marketDataPreference") return;
      loadTrades();
    };
    
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => {
      window.removeEventListener("tradesUpdated", handleTradesUpdated);
    };
  }, [user?.id, brokerAccountCode]);

  const openTrades = useMemo(() => {
    return trades.filter(trade => trade.status === "open");
  }, [trades]);

  const openTradesCount = openTrades.length;

  // Signals, system performance, and account/trade state (must be before activeAccountMetrics useMemo)
  const {
    accountMetrics,
    assetSignals,
    latestNoSignalReason,
    systemPerformance,
    loadSystemPerformance,
    upsertTakenTrades,
    replaceWithOpenTrades,
    clearTakenTrades,
    removeTakenTrades,
    removeSignal,
    ensureSignalsStatus,
  } = useSignalsContext();

  useEffect(() => {
    setTrades([]);
    clearTakenTrades();
  }, [brokerAccountCode, clearTakenTrades]);

  // Account metrics from WebSocket (account_update) for the active broker account
  const activeAccountMetrics = useMemo(() => {
    if (brokerAccountCode && accountMetrics?.[brokerAccountCode]) {
      return accountMetrics[brokerAccountCode];
    }
    // Fallback: use latest update when backend doesn't send account_code (e.g. single-account)
    return accountMetrics?.__latest;
  }, [accountMetrics, brokerAccountCode]);

  // Mini stat cards: account snapshot (page load) + WebSocket account_update
  const portfolioSize = useMemo(() => {
    if (activeAccountMetrics == null) return 0;
    const v = activeAccountMetrics.portfolio_balance ?? activeAccountMetrics.equity ?? activeAccountMetrics.balance;
    return typeof v === "number" ? v : 0;
  }, [activeAccountMetrics]);

  const portfolioChangePercent = useMemo(() => {
    if (activeAccountMetrics != null && typeof activeAccountMetrics.portfolio_change_percent === "number") {
      return activeAccountMetrics.portfolio_change_percent;
    }
    return null;
  }, [activeAccountMetrics?.portfolio_change_percent]);

  const unrealisedPnL = useMemo(() => {
    // Prefer broker-reported unrealized_pnl from WebSocket (no caching – updates every message)
    if (activeAccountMetrics && typeof activeAccountMetrics.unrealized_pnl === "number") {
      return activeAccountMetrics.unrealized_pnl;
    }

    return openTrades.reduce((total, trade) => {
      // Use netPnL if available (backend may calculate this for open trades)
      if (trade.netPnL !== null && trade.netPnL !== undefined) {
        return total + trade.netPnL;
      }
      // Fallback: calculate from entry value if we have the data
      if (trade.entryPrice && trade.entryQuantity) {
        // For open trades without current price, we can't calculate accurate unrealised PnL
        // Return 0 or use a placeholder - in production, this would come from broker API
        return total;
      }
      return total;
    }, 0);
  }, [activeAccountMetrics, openTrades]);

  const accountRisk = useMemo(() => {
    if (activeAccountMetrics && typeof activeAccountMetrics.account_risk_pct === "number") {
      return activeAccountMetrics.account_risk_pct;
    }
    // If backend hasn't provided account_risk_pct yet, treat risk as 0 rather than synthesizing a value
    return 0;
  }, [activeAccountMetrics]);

  // State to track which assets are currently displayed
  const initialActiveSymbols = mockAssetsData.map(asset => asset.symbol);
  const [activeAssetSymbols, setActiveAssetSymbols] = useState<string[]>(initialActiveSymbols);

  // Get all available assets (for selection)
  const allAvailableAssets: AssetData[] = useMemo(() => {
    return mockAssetsData.map((asset) => ({
      symbol: asset.symbol,
      price: asset.price,
      icon: <AssetIcon symbol={asset.symbol} />,
      chartData: generateChartData(asset.baseValue, asset.trend),
      baseValue: asset.baseValue,
    }));
  }, []);

  // Get only active assets
  const activeAssets: AssetData[] = useMemo(() => {
    return allAvailableAssets.filter(asset => 
      activeAssetSymbols.includes(asset.symbol)
    );
  }, [activeAssetSymbols, allAvailableAssets]);

  // Get assets that are not currently active (for selection modal)
  const availableToAdd: AssetData[] = useMemo(() => {
    return allAvailableAssets.filter(asset => 
      !activeAssetSymbols.includes(asset.symbol)
    );
  }, [activeAssetSymbols, allAvailableAssets]);

  // Load system performance when Tradar page loads; WebSocket updates when setups enabled
  useEffect(() => {
    loadSystemPerformance();
  }, [loadSystemPerformance]);

  // Re-fetch signals status when Tradar page mounts so WebSocket can connect if backend has signals enabled
  useEffect(() => {
    ensureSignalsStatus();
  }, [ensureSignalsStatus]);

  const [selectedSignal, setSelectedSignal] = useState<Signal | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<AssetData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Seed open trades from DB into context so Active Setups table can show them.
  // Replace (not merge) so that after refetch/close-all we only show current open trades.
  useEffect(() => {
    const openTradesFromDb = trades.filter(trade => trade.status === "open");
    replaceWithOpenTrades(openTradesFromDb);
  }, [trades, replaceWithOpenTrades]);

  // Close a single trade at broker (POST /api/trades/{trade_id}/close).
  // tradeId is the DB trade UUID from the row (Active Setups uses context, not local trades state).
  const handleCloseTrade = useCallback(async (tradeId: string) => {
    if (!tradeId) return;
    try {
      await closeTrade(tradeId);
      removeTakenTrades([tradeId]);
      notifyTradesUpdated({ action: "sync" });
      toast.success("Trade closed", { description: "Position closed at broker." });
    } catch (err: any) {
      presentMutationError(err, "Failed to close trade");
    }
  }, [removeTakenTrades]);

  // Close all open positions at broker (POST /api/broker/positions/close-all)
  const handleCloseAllTrades = useCallback(async () => {
    if (!brokerAccountCode) return;
    try {
      await closeAllTrades();
      notifyTradesUpdated({ action: "sync" });
      toast.success("All positions closed", { description: "All open positions have been closed at broker." });
    } catch (err: any) {
      presentMutationError(err, "Failed to close all");
    }
  }, [brokerAccountCode]);

  const handlePlaceOrder = async (
    signal: Signal,
    asset: AssetData,
    overrides?: {
      lotSize?: number;
      stopLossPrice?: number;
      takeProfitPrice?: number;
    }
  ) => {
    if (!brokerAccountCode) {
      toastWarning("Broker account not connected", { description: "Please connect your broker account before taking trades." });
      return;
    }

    try {
      // Parse prices from formatted strings
      const entryPrice = parseFloat(signal.entry.replace(/[$,]/g, ""));
      const tpPriceFromSignal = parseFloat(signal.tp.replace(/[$,]/g, ""));
      const slPriceFromSignal = parseFloat(signal.sl.replace(/[$,]/g, ""));

      // Lot size: use override from modal when provided; otherwise use a conservative default.
      const defaultLotSize = 0.01;
      const lotSizeToSend = overrides?.lotSize ?? defaultLotSize;

      // Backend now uses event_id as the only identifier; require it explicitly
      if (!signal.eventId) {
        toastError("Cannot execute trade", { description: "Missing backend event id for this signal. Please refresh signals and try again." });
        return;
      }

      const { trade } = await executeSignalTrade(signal.eventId, {
        lot_size: lotSizeToSend,
        broker_account_code: brokerAccountCode,
        stop_loss_price: overrides?.stopLossPrice ?? slPriceFromSignal,
        take_profit_price: overrides?.takeProfitPrice ?? tpPriceFromSignal,
      });

      // Keep setup visible so user can take it again if desired; it will disappear when it expires (SignalsContext interval).

      // Seed/update Taken Setups via shared context (Active Setups table reads from takenTrades)
      const frontendTrade = backendTradeToFrontendTrade(trade);
      upsertTakenTrades([{ ...frontendTrade, tags: [...(frontendTrade.tags || []), "tradar"] }]);

      toast.success("Trade executed", { description: `Trade for ${asset.symbol} has been placed.` });
    } catch (error: any) {
      console.error("Failed to execute trade from signal:", error);
      presentMutationError(error, "Failed to execute trade");
    }
  };
  
  // Construct broker trading URL with pre-filled trade parameters
  const constructBrokerTradeUrl = async (tradeParams: {
    symbol: string;
    type: string;
    entryPrice: number;
    takeProfit: number;
    stopLoss: number;
  }): Promise<string | null> => {
    try {
      // Dynamically import to avoid circular dependencies
      const { getBrokerCredentials } = require('../../../services/liquidBrokersApi');
      const credentials = await getBrokerCredentials('liquidbrokers');
      
      // If no broker connected, return null (don't open broker URL)
      if (!credentials) {
        return null;
      }
      
      // Construct LiquidBrokers trading URL
      // Note: This is a placeholder URL structure - actual broker URLs may vary
      // You may need to adjust this based on your broker's actual trading interface URL format
      const baseUrl = credentials.apiEndpoint 
        ? credentials.apiEndpoint.replace('/api', '').replace('/dxsca-fxblue', '')
        : 'https://trader.liquidcharts.com';
      
      // Try to construct the trading URL - adjust path and parameters based on broker's actual interface
      const tradingUrl = `${baseUrl}/dxsca-fxblue/trade`;
      
      // Build query parameters
      const params = new URLSearchParams({
        symbol: tradeParams.symbol,
        side: tradeParams.type === 'LONG' ? 'BUY' : 'SELL',
        price: tradeParams.entryPrice.toString(),
        tp: tradeParams.takeProfit.toString(),
        sl: tradeParams.stopLoss.toString(),
      });
      
      return `${tradingUrl}?${params.toString()}`;
    } catch (error) {
      console.error('Error constructing broker URL:', error);
      return null;
    }
  };

  const handleSignalClick = (signal: Signal, asset: AssetData) => {
    setSelectedSignal(signal);
    setSelectedAsset(asset);
    setIsModalOpen(true);
  };

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedSignal(null);
    setSelectedAsset(null);
  }, []);

  // Stable callback for the details modal so it doesn't re-render when parent updates (e.g. WebSocket/timer).
  const handleModalPlaceOrder = useCallback(
    (
      signal: Signal,
      overrides?: { lotSize?: number; stopLossPrice?: number; takeProfitPrice?: number }
    ) => {
      if (selectedAsset) handlePlaceOrder(signal, selectedAsset, overrides);
    },
    [selectedAsset, handlePlaceOrder]
  );

  const handleAddStream = (asset: AssetData) => {
    setActiveAssetSymbols(prev => [...prev, asset.symbol]);
    onClose();
    toast.success("Signal Stream Added", { description: `${asset.symbol} signal stream has been added.` });
  };

  const handleRemoveStream = (asset: AssetData) => {
    setActiveAssetSymbols(prev => prev.filter(symbol => symbol !== asset.symbol));
    toast.info("Signal Stream Removed", { description: `${asset.symbol} signal stream has been removed.` });
  };

  return (
    <RequireAdmin>
    <Flex
      direction="column"
      pt={shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" }}
    >
      <PageRouteHeadline mb="32px">Tradar</PageRouteHeadline>

      {/* Mini Statistics Cards */}
      <SimpleGrid 
        columns={{ base: 1, sm: 2, md: 2, lg: 4 }}
        spacing="24px"
        mb="32px"
      >
        <TradeLogMiniStatistics
          title="Portfolio Size"
          amount={formatMoney(
            activeAccountMetrics?.portfolio_balance ?? portfolioSize
          )}
          percentage={portfolioChangePercent ?? null}
          tooltip="The total current value of your trading portfolio, including all open positions and available cash."
          showPercentage={true}
        />
        <TradeLogMiniStatistics
          title="Unrealised P&L"
          amount={formatMoney(unrealisedPnL, { showSign: true })}
          percentage={null}
          tooltip="The total unrealised profit or loss from all currently open trades."
          showPercentage={false}
        />
        <TradeLogMiniStatistics
          title="Account Risk"
          amount={`${accountRisk.toFixed(2)}%`}
          percentage={null}
          backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
          tooltip="The total risk exposure of your account as a percentage of portfolio value, based on open positions."
          showPercentage={false}
        />
        <TradeLogMiniStatistics
          title="Today's P&L"
          amount={formatMoney(activeAccountMetrics?.todays_pnl ?? 0, { showSign: true })}
          percentage={null}
          tooltip="The total realised profit or loss from all trades closed today."
          showPercentage={false}
        />
      </SimpleGrid>

      <VStack
        align="stretch"
        spacing={6}
        w="100%"
      >
      {activeAssets.map((asset) => (
        <SignalStreamCard
          key={asset.symbol}
          asset={asset}
          signals={assetSignals[asset.symbol] || []}
          onPlaceOrder={handlePlaceOrder}
          onSignalClick={handleSignalClick}
          onRemove={handleRemoveStream}
          onCloseTrade={handleCloseTrade}
          onCloseAllTrades={handleCloseAllTrades}
          brokerAccountCode={brokerAccountCode}
          latestNoSignalReason={latestNoSignalReason}
          systemPerformance={systemPerformance}
        />
      ))}
      </VStack>
      
      {/* Add Signal Stream Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.1)" />
        <ModalContent bg="white" borderRadius="lg" maxH="90vh">
          <ModalHeader color="#38BBD1" fontFamily="'Urbanist', sans-serif" fontWeight="normal">
            Add Signal Stream
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto" maxH="calc(90vh - 140px)">
            {availableToAdd.length === 0 ? (
              <Text color="#575757" fontFamily="'IBM Plex Sans', sans-serif">
                All available assets are already displayed.
              </Text>
            ) : (
              <VStack align="stretch" spacing={3}>
                {availableToAdd.map((asset) => (
                  <Button
                    key={asset.symbol}
                    variant="outline"
                    borderColor="rgba(56, 187, 209, 0.3)"
                    color="#575757"
                    _hover={{
                      bg: "rgba(56, 187, 209, 0.1)",
                      borderColor: "rgba(56, 187, 209, 0.5)",
                    }}
                    onClick={() => handleAddStream(asset)}
                    justifyContent="flex-start"
                    h="auto"
                    py={4}
                    px={4}
                  >
                    <Flex align="center" gap={3} w="100%">
                      {asset.icon && (
                        <Box flexShrink={0}>
                          {asset.icon}
                        </Box>
                      )}
                      <VStack align="start" spacing={1} flex={1}>
                        <Text
                          fontSize="md"
                          fontWeight="bold"
                          color="#575757"
                          fontFamily="'IBM Plex Sans', sans-serif"
                        >
                          {asset.symbol}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="gray.400"
                          fontFamily="'IBM Plex Sans', sans-serif"
                        >
                          {asset.price}
                        </Text>
                      </VStack>
                    </Flex>
                  </Button>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      
      <SignalDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        signal={selectedSignal}
        assetSymbol={selectedAsset?.symbol || ""}
        onPlaceOrder={handleModalPlaceOrder}
      />
    </Flex>
    </RequireAdmin>
  );
}

export default Tables;

